import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { fonts as fontsApi } from '../api/client.js';

export default function DuplicatesPanel({ onClose, onDeleteFont }) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fontsApi.duplicates()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (fontId, groupIdx) => {
    if (!confirm('Delete this font? This cannot be undone.')) return;
    try {
      await fontsApi.delete(fontId);
      onDeleteFont?.(fontId);
      // Remove from local state
      setData(prev => {
        const updated = { ...prev };
        updated.duplicates = prev.duplicates.map((g, i) => {
          if (i !== groupIdx) return g;
          return { ...g, fonts: g.fonts.filter(f => f.id !== fontId) };
        }).filter(g => g.fonts.length >= 2);
        updated.total = updated.duplicates.length;
        return updated;
      });
    } catch (err) { console.error('Delete failed:', err); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 800, maxHeight: '85vh',
          background: theme.bgSecondary, borderRadius: 16,
          border: `1px solid ${theme.border}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.text }}>Duplicate Detection</h2>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
              {loading ? 'Scanning...' : `${data?.total || 0} duplicate groups found`}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: theme.bgInput, border: 'none', borderRadius: 8,
              color: theme.textMuted, width: 32, height: 32, cursor: 'pointer', fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >&#x2715;</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 13 }}>
              Analyzing font library for duplicates...
            </div>
          )}

          {!loading && (!data?.duplicates?.length) && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>&#x2713;</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>No duplicates found</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Your font library is clean</div>
            </div>
          )}

          {data?.duplicates?.map((group, gi) => (
            <div key={gi} style={{
              marginBottom: 16, padding: 16,
              background: theme.bgTertiary, borderRadius: 12,
              border: `1px solid ${theme.borderLight}`,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                  {group.family}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: theme.orange,
                    background: `${theme.orange}18`, padding: '3px 8px', borderRadius: 6,
                  }}>Score: {group.score}</span>
                  {group.reasons.map(r => (
                    <span key={r} style={{
                      fontSize: 9, color: theme.textMuted, background: theme.bgInput,
                      padding: '2px 6px', borderRadius: 4,
                    }}>{r}</span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {group.fonts.map(font => (
                  <div key={font.id} style={{
                    flex: '1 1 200px', padding: 12,
                    background: theme.cardBg, borderRadius: 8,
                    border: `1px solid ${theme.cardBorder}`,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                      {font.name}
                    </div>
                    <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>
                      {font.category} &middot; {font.style_count} styles &middot; {font.size_display}
                      {font.designer ? ` \u00B7 ${font.designer}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        style={{
                          flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 600,
                          background: theme.greenBg, border: `1px solid ${theme.greenBorder}`,
                          borderRadius: 6, color: theme.green, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >Keep</button>
                      <button
                        onClick={() => handleDelete(font.id, gi)}
                        style={{
                          flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 600,
                          background: theme.redBg, border: `1px solid ${theme.redBorder}`,
                          borderRadius: 6, color: theme.red, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
